import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './PDFHighlight.css';

function PDFHighlight({
  field,
  coordinates,
  pdfDimensions,
  containerDimensions,
  value,
  onUpdate,
  onActivate,
  lineItemIndex,
  activeField,
  activeLineItemIndex,
}) {
  // State management
  const [isEditing, setIsEditing] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [editedValue, setEditedValue] = useState(value);

  // Refs
  const inputRef = useRef(null);
  const highlightRef = useRef(null);

  const [x, y, width, height] = coordinates;

  // Styles for the highlight
  const highlightStyle = {
    left: `${(x / pdfDimensions.width) * 100}%`,
    top: `${((pdfDimensions.height - y - height) / pdfDimensions.height) * 100}%`,
    width: `${(width / pdfDimensions.width) * 100}%`,
    height: `${(height / pdfDimensions.height) * 100}%`,
    position: 'absolute',
    backgroundColor: 'rgba(0, 123, 255, 0.2)',
    border: '2px solid rgba(0, 123, 255, 0.5)',
    pointerEvents: 'auto',
    cursor: 'pointer',
    zIndex: 1000,
    borderRadius: '4px', // Rounded corners
    transition: 'transform 0.2s ease, background-color 0.2s ease',
  };

  // Handle click to activate editing
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onActivate(field, lineItemIndex);
  };

  // Handle input change
  const handleChange = (e) => {
    setEditedValue(e.target.value);
  };

  // Handle blur event
  const handleBlur = (e) => {
    // Check if the related target (the element receiving focus) is within the popup
    if (
      e.relatedTarget &&
      (e.relatedTarget.closest('.edit-popup') ||
        e.relatedTarget.closest('.pdf-highlight'))
    ) {
      // Focus moved within the popup or back to the highlight, do not close
      return;
    }

    // Update the field value and deactivate editing
    onUpdate(field, editedValue, lineItemIndex);
    onActivate(null, null); // Reset active field
  };

  // Manage editing state based on activeField and activeLineItemIndex
  useEffect(() => {
    if (
      activeField === field &&
      (activeLineItemIndex === lineItemIndex ||
        (activeLineItemIndex === null && lineItemIndex === null))
    ) {
      setIsEditing(true);
    } else {
      setIsEditing(false);
    }
  }, [activeField, activeLineItemIndex, field, lineItemIndex]);

  // Focus the input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  // Update editedValue when the prop 'value' changes
  useEffect(() => {
    setEditedValue(value);
  }, [value]);

  // Define popup dimensions as constants
  const POPUP_WIDTH = 220; // Width of the popup in pixels
  const POPUP_HEIGHT = 140; // Height of the popup in pixels
  const GAP = 40; // Gap between the popup and the highlighted field in pixels

  // Calculate position for the edit popup
  const [popupPosition, setPopupPosition] = useState({
    left: 0,
    top: 0,
    position: 'above', // 'above', 'below', 'left', 'right'
  });

  useEffect(() => {
    if (isEditing && highlightRef.current) {
      const highlightRect = highlightRef.current.getBoundingClientRect();

      // Calculate available space around the highlight
      const spaceAbove = highlightRect.top;
      const spaceBelow = window.innerHeight - highlightRect.bottom;
      const spaceLeft = highlightRect.left;
      const spaceRight = window.innerWidth - highlightRect.right;

      // Determine the best position for the popup
      let position = 'above';
      if (spaceAbove >= POPUP_HEIGHT + GAP) {
        position = 'above';
      } else if (spaceBelow >= POPUP_HEIGHT + GAP) {
        position = 'below';
      } else if (spaceRight >= POPUP_WIDTH + GAP) {
        position = 'right';
      } else if (spaceLeft >= POPUP_WIDTH + GAP) {
        position = 'left';
      } else {
        // Default to below if no space is sufficient, adjust height if necessary
        position = 'below';
      }

      // Calculate popup's left and top based on the chosen position
      let popupLeft, popupTop;

      switch (position) {
        case 'above':
          popupLeft = highlightRect.left + highlightRect.width / 2 - POPUP_WIDTH / 2;
          popupTop = highlightRect.top - POPUP_HEIGHT - GAP;
          break;
        case 'below':
          popupLeft = highlightRect.left + highlightRect.width / 2 - POPUP_WIDTH / 2;
          popupTop = highlightRect.bottom + GAP;
          break;
        case 'left':
          popupLeft = highlightRect.left - POPUP_WIDTH - GAP;
          popupTop = highlightRect.top + highlightRect.height / 2 - POPUP_HEIGHT / 2;
          break;
        case 'right':
          popupLeft = highlightRect.right + GAP;
          popupTop = highlightRect.top + highlightRect.height / 2 - POPUP_HEIGHT / 2;
          break;
        default:
          popupLeft = highlightRect.left + highlightRect.width / 2 - POPUP_WIDTH / 2;
          popupTop = highlightRect.bottom + GAP;
      }

      // Ensure the popup doesn't go off-screen horizontally
      if (popupLeft < 10) {
        popupLeft = 10;
      } else if (popupLeft + POPUP_WIDTH > window.innerWidth - 10) {
        popupLeft = window.innerWidth - POPUP_WIDTH - 10;
      }

      // Ensure the popup doesn't go off-screen vertically
      if (popupTop < 10) {
        popupTop = 10;
      } else if (popupTop + POPUP_HEIGHT > window.innerHeight - 10) {
        popupTop = window.innerHeight - POPUP_HEIGHT - 10;
      }

      setPopupPosition({ left: popupLeft, top: popupTop, position });
    }
  }, [isEditing]);

  // Create the editing popup as a portal
  const editPopup = isEditing
    ? createPortal(
        <div
          className={`edit-popup ${popupPosition.position}`}
          style={{
            position: 'fixed',
            left: `${popupPosition.left}px`,
            top: `${popupPosition.top}px`,
            zIndex: 2000,
            width: `${POPUP_WIDTH}px`,
            height: `${POPUP_HEIGHT}px`,
            overflow: 'hidden',
          }}
          role="dialog"
          aria-labelledby={`edit-popup-label-${field}`}
          aria-modal="true"
        >
          <div className="edit-popup-label" id={`edit-popup-label-${field}`}>
            {field}
          </div>
          <input
            ref={inputRef}
            type="text"
            value={editedValue}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={`Edit ${field}`}
          />
          <div className="edit-popup-buttons">
            <button onClick={handleBlur} aria-label="Save">
              ✓
            </button>
            <button
              className="cancel-button"
              onClick={() => onActivate(null, null)}
              aria-label="Cancel"
            >
              ✕
            </button>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <div
        ref={highlightRef}
        className="pdf-highlight"
        data-field={field}
        data-lineitemindex={lineItemIndex ?? ''}
        style={highlightStyle}
        onClick={handleClick}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {isHovering && !isEditing && (
          <div className="highlight-tooltip">{field}</div>
        )}
      </div>
      {editPopup}
    </>
  );
}

export default PDFHighlight;
